import React from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Button, Flex, Text, Tooltip } from 'core/components';

import KaizenChatSession from 'app/views/core/kaizen/KaizenChatSession';
import { AiOutlineArrowLeft, AiOutlineClose, AiOutlineExpand, AiOutlineExport } from 'react-icons/ai';

import { withRouter } from 'react-router-dom';

import KaizenChatList from 'app/views/core/kaizen/KaizenChatList';
import { InputGroup } from 'core/form';
import { FiPlus } from 'react-icons/fi';
import { BiHistory } from 'react-icons/bi';
import McpAuthRequiredBanner from 'app/views/core/kaizen/components/McpAuthRequiredBanner';
import McpStatusIndicator from 'app/views/core/kaizen/components/McpStatusIndicator';
import { createToolResultComponent } from 'app/views/core/kaizen/ToolResultFactory';
import KaizenCopilotInput from './KaizenCopilotInput';
import KaizenCopilotSuggestions from './KaizenCopilotSuggestions';
import {
  AnimatedCard,
  COPILOT_CONFIG,
  ResizeHandle,
  ScrollContainer,
  ScrollToBottomButton
} from './KaizenCopilot.styles';

const DEFAULT_SUGGESTIONS = ['Whats going on with my network?', 'Summarize active alerts', 'Help me with this page'];

const TOOLTIP_CONFIG = {
  position: 'top',
  boundary: 'viewport',
  usePortal: true
};

@inject('$kaizen')
@withRouter
@observer
class KaizenCopilot extends React.Component {
  state = {
    value: '',
    isScrolledToBottom: true,
    isExistingChatLoading: false,
    isShowingChatSelection: false,
    selectedCopilotToolResult: null,
    width: COPILOT_CONFIG.DIMENSIONS.DEFAULT_WIDTH,
    height: COPILOT_CONFIG.DIMENSIONS.DEFAULT_HEIGHT,
    isResizing: false,
    resizeType: null,
    showScrollToBottomButton: false,
    searchInputValue: ''
  };

  scrollAreaRef = React.createRef();

  updatePageContext = (pathname) => {
    const { $kaizen } = this.props;

    // Remove /v4/ prefix from pathname for cleaner context
    const cleanPathname = pathname.replace(/^\/v4\//, '');

    // Add or update the page URL context
    $kaizen.addChatContext({
      type: 'page',
      name: 'page:current',
      context: `Current page URL: ${cleanPathname}`,
      hidden: true,
      simple: true
    });
  };

  componentDidMount() {
    const { $kaizen } = this.props;
    $kaizen.chatSessionCollection.fetch();
    $kaizen.subscribeToHeartbeats();

    // Add scroll event listener
    if (this.scrollAreaRef.current) {
      this.scrollAreaRef.current.addEventListener('scroll', this.handleScroll);
      // Initial scroll check
      setTimeout(this.handleScroll, 100);
    }
  }

  componentWillUnmount() {
    const { $kaizen } = this.props;
    $kaizen.unsubscribeFromHeartbeats();
    this.removeResizeListeners();

    // Clean up scroll event listener
    if (this.scrollAreaRef.current) {
      this.scrollAreaRef.current.removeEventListener('scroll', this.handleScroll);
    }
  }

  // convert to px
  parseDimension = (value) => {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      if (value.endsWith('vw')) {
        return (parseFloat(value) / 100) * window.innerWidth;
      }
      if (value.endsWith('vh')) {
        return (parseFloat(value) / 100) * window.innerHeight;
      }
      if (value.endsWith('px')) {
        return parseFloat(value);
      }
    }

    return typeof value === 'number' ? value : null;
  };

  get presetDimensions() {
    const { $kaizen } = this.props;

    const presetWidth = this.parseDimension($kaizen.copilotDimensions.width);
    const presetHeight = this.parseDimension($kaizen.copilotDimensions.height);

    return {
      presetWidth: presetWidth && Math.max(presetWidth, COPILOT_CONFIG.DIMENSIONS.MIN_WIDTH),
      presetHeight: presetHeight && Math.max(presetHeight, COPILOT_CONFIG.DIMENSIONS.MIN_HEIGHT)
    };
  }

  addResizeListeners = () => {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  };

  removeResizeListeners = () => {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  };

  // does not clear the chat session, just minimizes the UI
  handleMinimize = () => {
    const { $kaizen } = this.props;
    $kaizen.minimizeCopilot();
  };

  handleSelectToolResult = (tool) => {
    this.setState({ selectedCopilotToolResult: tool });
  };

  handleCloseToolResult = () => {
    this.setState({ selectedCopilotToolResult: null });
  };

  handleToggleChatSelection = () => {
    const { $kaizen } = this.props;

    $kaizen.chatSessionCollection?.clearFilters();

    this.setState((prevState) => ({
      isShowingChatSelection: !prevState.isShowingChatSelection,
      searchInputValue: ''
    }));
  };

  handleExportPdf = () => {
    const { $kaizen } = this.props;
    const sessionId = $kaizen.chatSessionCollection?.selected?.get('id');
    if (sessionId) {
      $kaizen.exportPdf(sessionId);
    }
  };

  handleNavigateToJourney = () => {
    const { $kaizen, history } = this.props;
    const id = $kaizen.chatSessionCollection?.selected?.get('id');
    history.push(`${$kaizen.baseUrl}/${id}`);
    $kaizen.closeCopilot();
  };

  handleSend = (value) => {
    const { $kaizen, location } = this.props;

    this.updatePageContext(location.pathname);

    const sent = $kaizen.sendMessage(value);
    this.setState({ value: '' });
    this.scrollToBottom();

    return sent;
  };

  handleCancel = () => {
    const { $kaizen } = this.props;
    return $kaizen.chatSessionCollection.selected.cancelWaitingTurn();
  };

  handleSuggestionClick = (suggestion) => {
    this.setState({ value: suggestion });
    this.handleSend(suggestion);
  };

  handleStartNewChat = () => {
    const { $kaizen } = this.props;

    // don't need to do anything else here other than clear the selection
    // `handleSend` will handle creating a new journey
    $kaizen.chatSessionCollection.clearSelection();
  };

  handleSelectJourney = (journey) => {
    this.setState({ isExistingChatLoading: true, isShowingChatSelection: false });
    journey.selectAndSubscribe({ fetch: true }).then(() => {
      this.setState({ isExistingChatLoading: false });
    });
  };

  scrollToBottom = () => {
    if (this.scrollAreaRef?.current) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        const element = this.scrollAreaRef.current;
        if (element) {
          element.scrollTo({
            top: element.scrollHeight,
            behavior: 'smooth'
          });
          // Check scroll position after scrolling
          setTimeout(this.handleScroll, 100);
        }
      });
    }
  };

  handleMouseDown = (e, resizeType) => {
    const { $kaizen } = this.props;
    e.preventDefault();
    e.stopPropagation();
    this.addResizeListeners();

    const { presetWidth, presetHeight } = this.presetDimensions;

    this.setState((prevState) => ({
      isResizing: true,
      resizeType,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: presetWidth || prevState.width,
      startHeight: presetHeight || prevState.height,
      width: presetWidth || prevState.width,
      height: presetHeight || prevState.height
    }));
    // clear preset dimensions so user control takes over
    $kaizen.clearCopilotDimensions();
  };

  calculateNewDimensions = (deltaX, deltaY, resizeType, startWidth, startHeight) => {
    let newWidth = startWidth;
    let newHeight = startHeight;

    // Calculate max dimensions based on viewport
    const maxWidth = window.innerWidth - COPILOT_CONFIG.POSITION.RIGHT * 2;
    const maxHeight = window.innerHeight - COPILOT_CONFIG.POSITION.BOTTOM * 2;

    if (resizeType === 'left' || resizeType === 'top-left') {
      newWidth = Math.max(COPILOT_CONFIG.DIMENSIONS.MIN_WIDTH, Math.min(maxWidth, startWidth - deltaX));
    }

    if (resizeType === 'top' || resizeType === 'top-left') {
      newHeight = Math.max(COPILOT_CONFIG.DIMENSIONS.MIN_HEIGHT, Math.min(maxHeight, startHeight - deltaY));
    }

    return { newWidth, newHeight };
  };

  handleMouseMove = (e) => {
    const { isResizing, resizeType, startX, startY, startWidth, startHeight } = this.state;

    if (!isResizing) {
      return;
    }

    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    const { newWidth, newHeight } = this.calculateNewDimensions(deltaX, deltaY, resizeType, startWidth, startHeight);

    this.setState({ width: newWidth, height: newHeight });
  };

  handleMouseUp = () => {
    const { isResizing } = this.state;
    if (isResizing) {
      this.removeResizeListeners();
      this.setState({
        isResizing: false,
        resizeType: null,
        startX: undefined,
        startY: undefined,
        startWidth: undefined,
        startHeight: undefined
      });
    }
  };

  handleScroll = () => {
    if (!this.scrollAreaRef.current) {
      return;
    }

    const container = this.scrollAreaRef.current;
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50; // 50px threshold

    this.setState({ showScrollToBottomButton: !isAtBottom });
  };

  handleScrollToBottomClick = () => {
    this.scrollToBottom();
  };

  renderChatSelection = () => {
    const { $kaizen } = this.props;

    if (!$kaizen.chatSessionCollection) {
      return null;
    }

    return (
      <Flex flexDirection="column" gap={2} overflow="auto" bg="cardBackground" p="12px" height="100%">
        <KaizenChatList
          title="Recent"
          isCopilotMode
          onSelectJourney={this.handleSelectJourney}
          hideUserName
          hideShareLevel
          hideEditedDate={false}
          journeys={$kaizen.chatSessionCollection.userChats}
          maxInitialItems={10}
        />
        <KaizenChatList
          title="Company"
          isCopilotMode
          onSelectJourney={this.handleSelectJourney}
          hideShareLevel
          hideEditedDate={false}
          journeys={$kaizen.chatSessionCollection.companyChats}
          maxInitialItems={10}
        />
      </Flex>
    );
  };

  // Memoized calculations
  getSuggestions = () => {
    const { $kaizen } = this.props;
    const suggestions = [...DEFAULT_SUGGESTIONS];

    if ($kaizen.activeChatContext.size > 0) {
      suggestions.push(...Array.from($kaizen.activeChatContext).flatMap((context) => context.suggestions || []));
    }

    return suggestions;
  };

  getTitle = (selectedChatSession) => {
    const sessionTitle = selectedChatSession?.chatSessionTitle;
    return sessionTitle && sessionTitle !== 'AI Advisor' && sessionTitle !== '' ? sessionTitle : 'AI Advisor';
  };

  handleSearchInputChange = (e) => {
    const { $kaizen } = this.props;
    const { value = '' } = e.target;
    this.setState({ searchInputValue: value });
    $kaizen.chatSessionCollection.searchChats(value);
  };

  handleTransitionEnd = (e) => {
    const { $kaizen } = this.props;

    // Only reset on opacity transition completion when closing
    if (e.propertyName === 'opacity' && !$kaizen.isCopilotOpen) {
      $kaizen.clearCopilotDimensions();
    }
  };

  render() {
    const { $kaizen } = this.props;
    const {
      value,
      isScrolledToBottom,
      isExistingChatLoading,
      isShowingChatSelection,
      width,
      height,
      showScrollToBottomButton,
      searchInputValue,
      selectedCopilotToolResult
    } = this.state;

    const { presetWidth, presetHeight } = this.presetDimensions;
    const suggestions = this.getSuggestions();
    const selectedChatSession = $kaizen.chatSessionCollection?.selected;
    const hasMessages = selectedChatSession?.get('messages')?.length > 0;
    const title = this.getTitle(selectedChatSession);

    return (
      <AnimatedCard
        className={$kaizen.isCopilotOpen ? 'visible' : 'hidden'}
        elevation={4}
        style={{
          width: `${presetWidth || width}px`,
          height: `${presetHeight || height}px`
        }}
        onTransitionEnd={this.handleTransitionEnd}
      >
        <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <ResizeHandle className="resizer resize-top" onMouseDown={(e) => this.handleMouseDown(e, 'top')} />
          <ResizeHandle className="resizer resize-left" onMouseDown={(e) => this.handleMouseDown(e, 'left')} />
          <ResizeHandle className="resizer resize-top-left" onMouseDown={(e) => this.handleMouseDown(e, 'top-left')} />

          <Flex alignItems="center" justifyContent="space-between" p={2} borderBottom="thin">
            <Flex alignItems="center" gap="8px">
              {(isShowingChatSelection || selectedCopilotToolResult) && (
                <>
                  <Button
                    icon={AiOutlineArrowLeft}
                    onClick={selectedCopilotToolResult ? this.handleCloseToolResult : this.handleToggleChatSelection}
                    text="Back"
                    tracker="ai-advisor-button-back"
                  />
                </>
              )}
              {!isShowingChatSelection && !selectedCopilotToolResult && <Text fontWeight="bold">{title}</Text>}
            </Flex>
            <Flex alignItems="center" gap="8px">
              {selectedChatSession && !isShowingChatSelection && (
                <>
                  <Tooltip content="New conversation" {...TOOLTIP_CONFIG}>
                    <Button
                      minimal
                      icon={FiPlus}
                      onClick={this.handleStartNewChat}
                      tracker="ai-advisor-button-new-chat"
                    />
                  </Tooltip>
                  <Box width="1px" height="20px" bg="#e1e5e9" />
                </>
              )}

              {isShowingChatSelection && (
                <InputGroup
                  autoFocus
                  leftIcon="search"
                  placeholder="Search..."
                  value={searchInputValue}
                  onChange={this.handleSearchInputChange}
                  type="search"
                />
              )}

              <Tooltip content="View history" disabled={isShowingChatSelection} {...TOOLTIP_CONFIG}>
                <Button
                  minimal
                  tracker="ai-advisor-button-toggle-history"
                  icon={BiHistory}
                  onClick={this.handleToggleChatSelection}
                  disabled={isShowingChatSelection}
                />
              </Tooltip>

              <McpStatusIndicator />

              {hasMessages && selectedChatSession && (
                <Tooltip content="Export conversation as PDF" disabled={isShowingChatSelection} {...TOOLTIP_CONFIG}>
                  <Button
                    minimal
                    icon={AiOutlineExport}
                    tracker="ai-advisor-button-export-pdf"
                    disabled={isShowingChatSelection}
                    onClick={this.handleExportPdf}
                  />
                </Tooltip>
              )}

              {hasMessages && selectedChatSession && (
                <Tooltip content="Expand to full page" disabled={isShowingChatSelection} {...TOOLTIP_CONFIG}>
                  <Button
                    minimal
                    icon={AiOutlineExpand}
                    tracker="ai-advisor-button-goto-full-page"
                    disabled={isShowingChatSelection}
                    onClick={this.handleNavigateToJourney}
                  />
                </Tooltip>
              )}

              <Tooltip content="Close" {...TOOLTIP_CONFIG}>
                <Button minimal icon={AiOutlineClose} onClick={this.handleMinimize} tracker="ai-advisor-button-close" />
              </Tooltip>
            </Flex>
          </Flex>

          {/* no justifyContent="flex-end": a chat taller than the view would overflow past the top, out of scroll reach */}
          <ScrollContainer
            flexDirection="column"
            ref={this.scrollAreaRef}
            flex="1"
            overflowY="auto"
            overflowX="hidden"
            bg="decks.background"
          >
            {isShowingChatSelection && this.renderChatSelection()}

            {selectedCopilotToolResult && selectedChatSession?.get('authRequiredTools')?.length > 0 && (
              <McpAuthRequiredBanner authRequiredTools={selectedChatSession.get('authRequiredTools')} />
            )}

            {selectedCopilotToolResult &&
              createToolResultComponent(selectedCopilotToolResult, {
                onCloseToolResult: this.handleCloseToolResult,
                isInlineMode: true
              })}

            {!isShowingChatSelection && !selectedCopilotToolResult && hasMessages && (
              <KaizenChatSession
                isCopilotMode
                chatSessionModel={selectedChatSession}
                scrollToBottom={this.scrollToBottom}
                isScrolledToBottom={isScrolledToBottom}
                isExistingChatLoading={isExistingChatLoading}
                onSendToExplorer={$kaizen.sendToExplorer}
                onSelectToolResult={this.handleSelectToolResult}
              />
            )}

            {!isShowingChatSelection && !selectedCopilotToolResult && !hasMessages && (
              <KaizenCopilotSuggestions
                suggestions={suggestions}
                onSuggestionClick={this.handleSuggestionClick}
                activeChatContext={$kaizen.activeChatContext}
              />
            )}
          </ScrollContainer>

          <ScrollToBottomButton $isVisible={showScrollToBottomButton}>
            <Button icon="chevron-down" onClick={this.handleScrollToBottomClick} />
          </ScrollToBottomButton>

          <Box p={2} borderTop="thin">
            <KaizenCopilotInput
              value={value}
              onSubmit={this.handleSend}
              onRemoveContext={$kaizen.removeChatContext}
              activeChatContext={$kaizen.activeChatContext}
              isOpen={$kaizen.isCopilotOpen}
              submitBlocked={selectedChatSession?.isAnswerPaused}
              awaitingResponse={selectedChatSession?.hasToolAwaitingApproval}
              onCancel={selectedChatSession?.waitingTurn ? this.handleCancel : undefined}
            />
          </Box>
        </div>
      </AnimatedCard>
    );
  }
}

export default KaizenCopilot;
